import type { DatePickerMode } from '@shared/types/date';

import { OptionType } from '@shadcn-features/core/ShadcnForm/fields/ShadcnFormSelect';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { TFunction } from 'i18next';
import { ReactNode } from 'react';

dayjs.extend(isoWeek);
dayjs.extend(quarterOfYear);

const sampleDate = dayjs();

function getIsoWeekYear(value: dayjs.Dayjs) {
    return value.isoWeekYear();
}

function formatQuarterExample(t: TFunction) {
    return `${sampleDate.year()} - Q${sampleDate.quarter()} ${t('translation:quarter')}`;
}

function formatWeekExample(t: TFunction) {
    return `${getIsoWeekYear(sampleDate)} - ${sampleDate.isoWeek().toString().padStart(2, '0')} ${t('translation:week')}`;
}

function getFormatExample(
    dateType: DatePickerMode,
    format: string,
    t: TFunction,
) {
    if (dateType === 'quarter') {
        return formatQuarterExample(t);
    }

    if (dateType === 'week') {
        return formatWeekExample(t);
    }

    if (dateType === 'time' && format.includes('A')) {
        return sampleDate.locale('en').format(format);
    }

    return sampleDate.format(format);
}

function createOption(
    dateType: DatePickerMode,
    template: string,
    value: string,
    t: TFunction,
): OptionType {
    const example = getFormatExample(dateType, value, t);

    return {
        data: template,
        label: (
            <div className='flex flex-col py-1 leading-tight'>
                <span className='font-medium text-(--color-select-text)'>
                    {template}
                </span>
                <span className='text-xs text-(--color-card-subtitle)'>
                    {example}
                </span>
            </div>
        ),
        value,
    };
}

export function getProcessFieldDateFormatOptions(
    t: TFunction,
): Record<DatePickerMode, OptionType[]> {
    return {
        year: [
            createOption('year', 'YY', 'YY', t),
            createOption('year', 'YYYY', 'YYYY', t),
        ],
        quarter: [
            createOption(
                'quarter',
                `${t('translation:YYYYQ')}`,
                `YYYY - Q [${t('translation:quarter')}]`,
                t,
            ),
        ],
        month: [
            createOption('month', 'M', 'M', t),
            createOption('month', 'MM', 'MM', t),
            createOption('month', 'MMM', 'MMM', t),
            createOption('month', 'MMMM', 'MMMM', t),
            createOption('month', 'MMMM YYYY', 'MMMM YYYY', t),
        ],
        week: [
            createOption(
                'week',
                `YYYY - WW ${t('translation:week')}`,
                `GGGG - WW [${t('translation:week')}]`,
                t,
            ),
        ],
        date: [
            createOption('date', 'DD.MM.YYYY', 'DD.MM.YYYY', t),
            createOption('date', 'D', 'd', t),
            createOption('date', 'DD', 'dd', t),
            createOption('date', 'DDD', 'ddd', t),
            createOption('date', 'DDDD', 'dddd', t),
        ],
        time: [
            createOption('time', 'HH:mm', 'HH:mm', t),
            createOption('time', 'HH:mm:ss', 'HH:mm:ss', t),
            createOption('time', 'hh:mm A', 'hh:mm A', t),
            createOption('time', 'hh:mm:ss A', 'hh:mm:ss A', t),
        ],
        datetime: [
            createOption('datetime', 'DD.MM.YYYY HH:mm', 'DD.MM.YYYY HH:mm', t),
        ],
    };
}

export function renderProcessFieldDateFormatValue(
    _value: string | undefined,
    option: OptionType | undefined,
): ReactNode {
    return option?.data;
}
